import React, { useEffect, useState, useRef, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrance } from '../components/TranceContext';
import { motion } from 'framer-motion';
import ParticleNetwork from '../components/ParticleNetwork';
import { X, Compass } from 'lucide-react';
import { Button } from '../components/ui/button';
import ModeSelectionModal from '../components/ModeSelectionModal';

// Lazy load Spline for performance
const Spline = lazy(() => import('@splinetool/react-spline'));

const TrancePage = () => {
  const { isTranceMode, toggleTranceMode } = useTrance();
  const [isLoading, setIsLoading] = useState(true);
  const [isModeModalOpen, setIsModeModalOpen] = useState(false);
  const hasMountedRef = useRef(false);
  const navigate = useNavigate();

  // Ensure Trance Mode is active when landing here (only on mount)
  useEffect(() => {
    if (!hasMountedRef.current && !isTranceMode) {
      toggleTranceMode();
      hasMountedRef.current = true;
    }
  }, []);

  function onLoad() {
    setIsLoading(false);
  }

  const handleClose = () => {
    // Turn off audio before navigating away
    if (isTranceMode) {
      toggleTranceMode();
    }
    navigate('/');
  };

  return (
    <div className="min-h-screen w-full bg-black flex items-center justify-center relative overflow-hidden">
      
      {/* Background Forest Image - Opacity 10% */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1632231115662-3eed76916031?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxwc3l0cmFuY2UlMjBmb3Jlc3R8ZW58MHx8fHwxNzYzODU1NTAxfDA&ixlib=rb-4.1.0&q=85" 
          alt="Trance Forest Background" 
          className="w-full h-full object-cover opacity-10"
        />
      </div>

      {/* Floating Particles Network - Below 3D Orb */}
      <div className="absolute inset-0 z-5">
        <ParticleNetwork />
      </div>

      {/* Buttons */}
      <div className="absolute top-6 right-6 z-50 flex gap-3">
        <Button
          onClick={() => setIsModeModalOpen(true)}
          variant="outline"
          className="bg-black/50 backdrop-blur-sm border-white/20 hover:bg-gradient-to-r hover:from-cyan-500/20 hover:via-purple-500/20 hover:to-pink-500/20 hover:border-white/40 transition-all duration-300 font-heading tracking-wider"
        >
          <Compass className="w-4 h-4 mr-2" />
          CHOOSE YOUR PATH
        </Button>
        
        <Button
          onClick={handleClose}
          variant="outline"
          className="bg-black/50 backdrop-blur-sm border-white/20 hover:bg-white/10 hover:border-primary/50 transition-all duration-300 font-heading tracking-wider"
        >
          PARTY MODE OFF
        </Button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-50">
          <div className="text-primary font-heading tracking-widest animate-pulse text-xl">
            LOADING PARTY...
          </div>
        </div>
      )}

      {/* 3D Spline Orb - In Foreground */}
      <div className="w-full h-[100vh] relative z-10">
        <Suspense fallback={null}>
          <Spline 
            scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode"
            onLoad={onLoad}
            className={isLoading ? 'opacity-0' : 'opacity-100 transition-opacity duration-1000'}
          />
        </Suspense>
      </div>

      {/* Optional Overlay Text - Kept for context but z-indexed above */}
      <motion.div 
        className="absolute bottom-10 left-0 right-0 text-center pointer-events-none z-20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
      >
        <h1 className="text-4xl md:text-6xl font-heading font-black text-transparent bg-clip-text bg-gradient-to-b from-white to-primary/50 tracking-widest opacity-50">
          PARTY MODE
        </h1>
      </motion.div>

      {/* Mode Selection Modal */}
      <ModeSelectionModal isOpen={isModeModalOpen} onClose={() => setIsModeModalOpen(false)} />
    </div>
  );
};

export default TrancePage;
